import React from 'react';
import type { ButtonV2Props } from '@withjoy/joykit';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useGuestIdentity } from '@shared/utils/eventInfo/useGuestIdentity';
import { PeoplePickerButton } from '@shared/components/PeoplePicker';
import { ContactPickerButton } from './ContactPickerButton';
import type { HardcodedLabel, HardcodedLabelsMode } from '../PeoplePicker/usePeoplePickerButtonController';
import type { DeliveryMethodValue, PeoplePickerUseCase, RenderPickerTrigger } from '../PeoplePicker/PeoplePicker.types';
import type { OutsideScopeCopy } from '../PeoplePicker/PeoplePicker.types';
import type { SmartAudienceConfig, SmartAudienceOption } from '../PeoplePicker/smartAudience/smartAudience.types';
import { useSmartAudiences } from '../PeoplePicker/smartAudience/useSmartAudiences';

export type GuestPickerButtonProps = Readonly<
  Omit<ButtonV2Props, 'onClick' | 'children' | 'title' | 'itemType'> & {
    buttonTextDefault?: string;
    buttonTextHover?: string;
    title?: React.ReactNode;
    hardcodedLabels?: ReadonlyArray<HardcodedLabel>;
    hardcodedLabelsMode?: HardcodedLabelsMode;
    outsideScopeCopy?: OutsideScopeCopy;
    /** Copy for the guest list's select-all control in `requiredScope` mode. See PeoplePicker. */
    selectAllLabel?: string;
    onStartSelection?: () => void;
    useCase: PeoplePickerUseCase;
    // Forwarded to the underlying picker button to swap the default ButtonV2 for custom
    // trigger UI (e.g. recipient chips); the supplied onClick opens the picker.
    renderTrigger?: RenderPickerTrigger;
    headerBanner?: React.ReactNode;
    /**
     * Offer these smart tags alongside guests and tags (MSG-245). A pick lands in PeopleContext's
     * `smartRule` instead of `selectedPeople`, so only pass this from a flow that persists that rule on
     * its drafts — anywhere else the host's choice would silently send to nobody.
     */
    smartAudience?: SmartAudienceConfig;
  } & (
      | {
          itemType?: 'simple';
          enableSmsSelection?: never;
          smsOnly?: never;
          emailOnly?: never;
          initialDeliveryMethod?: never;
          allowUnreachableSelection?: never;
          preferenceStorageKey?: never;
          showDeliveryMethodSelector?: never;
          showPhoneChannel?: never;
        }
      | {
          itemType: 'withDeliveryMethod';
          enableSmsSelection: boolean;
          smsOnly?: boolean;
          emailOnly?: boolean;
          initialDeliveryMethod?: DeliveryMethodValue;
          allowUnreachableSelection?: boolean;
          preferenceStorageKey?: string;
          showDeliveryMethodSelector?: boolean;
          /** Show the WhatsApp or SMS channel per guest (MSG-584). Pass the `useIsSmsEnabled` result. */
          showPhoneChannel?: boolean;
        }
    )
>;

// Intersections rather than `Omit`: `Omit` over the `itemType` union collapses it, and the picker
// buttons need the discriminant intact.
type RoutedPickerProps = GuestPickerButtonProps & Readonly<{ smartAudiences?: ReadonlyArray<SmartAudienceOption>; smartAudiencesLoading?: boolean }>;

const RoutedPickerButton: React.FC<RoutedPickerProps> = ({ smartAudiences, smartAudiencesLoading, smartAudience: _smartAudience, ...props }) => {
  const { eventInfo, eventHandle = '' } = useEventInfo();
  // Shared with the draft and send paths so the three cannot disagree: the ContactPicker carries the
  // guestKey in `personIdTag`, and a consumer that still keyed off event type would read `id` — a
  // contact id — and label it a personId. See `resolveGuestIdentity` (MGT-69/MGT-70).
  const guestIdentity = useGuestIdentity();

  if (guestIdentity === 'guestKey' && eventInfo?.eventId) {
    return (
      <ContactPickerButton
        {...props}
        eventId={eventInfo.eventId}
        smartAudiences={smartAudiences}
        smartAudiencesLoading={smartAudiencesLoading}
      />
    );
  }

  // Anything other than a settled `personId` means the legacy picker is either wrong for this event
  // or not yet known to be right, and its roster is empty for a Firebase-free event — so a click
  // during that window opens a dialog showing zero guests. Disabled rather than swapped, so the
  // control doesn't move under the cursor once the identity settles.
  return (
    <PeoplePickerButton
      {...props}
      eventHandle={eventHandle}
      smartAudiences={smartAudiences}
      smartAudiencesLoading={smartAudiencesLoading}
      disabled={props.disabled || guestIdentity !== 'personId'}
    />
  );
};

const SmartAudiencePickerButton: React.FC<GuestPickerButtonProps & Readonly<{ smartAudience: SmartAudienceConfig }>> = ({ smartAudience, ...props }) => {
  const { eventInfo } = useEventInfo();
  const { smartAudiences, loading } = useSmartAudiences({ eventId: eventInfo?.eventId, config: smartAudience });
  return (
    <RoutedPickerButton
      {...props}
      smartAudiences={smartAudiences}
      smartAudiencesLoading={loading}
    />
  );
};

export const GuestPickerButton: React.FC<GuestPickerButtonProps> = ({ smartAudience, ...props }) =>
  // Split so a picker that doesn't offer smart tags never mounts their query at all.
  smartAudience ? (
    <SmartAudiencePickerButton
      {...props}
      smartAudience={smartAudience}
    />
  ) : (
    <RoutedPickerButton {...props} />
  );
